//@flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import Close from 'dibs-vg/dist/react/close';
import throttle from 'lodash.throttle';

import { TimeoutBar } from './TimeoutBar';
import { removeToast, getDomNode, popToast, updateToasterPosition } from './toaster';
import stylesheet from './main.scss';

const DEFAULT_TIMEOUT = 5000;

// stageDirections
const ENTERING = 'entering';
const EXITING = 'exiting';
const CLOSING = 'closing';

export type ToastType = 'info' | 'trade' | 'success' | 'error';

export type ToastPosition = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

type ExitStageDirections = 'exiting' | 'closing';

type StageDirections = 'entering' | ExitStageDirections;

export type ToastProps = {|
    children: React.Node,
    showCloseButton?: boolean,
    closeAfterTimeout?: boolean | number,
    type?: ToastType,
    position?: ToastPosition,
    isVisible?: boolean,
    onClose?: () => void,
|};

type ToastState = {|
    stageDirection: ?StageDirections,
    timeoutID: ?TimeoutID,
    showTimeoutBar: boolean,
    duration: ?number,
    toastNode: ?HTMLElement,
    hasEntered: boolean,
|};

function CloseButton({ handleClose, type }: { handleClose: () => void, type?: ToastType }) {
    return (
        <button
            aria-label="Close"
            onClick={handleClose}
            className={classnames(stylesheet.closeButton, stylesheet[type])}
        >
            <Close className={stylesheet.closeIcon} />
        </button>
    );
}

export class Toast extends React.Component<ToastProps, ToastState> {
    static defaultProps = {
        showCloseButton: true,
        closeAfterTimeout: false,
        position: 'bottomRight',
        type: 'info',
        isVisible: true,
    };

    constructor(props: ToastProps) {
        super(props);
        this.throttledHandleScroll = throttle(this.handleScroll, 10, {
            trailing: true,
        });
    }

    state = {
        timeoutID: null,
        showTimeoutBar: false,
        stageDirection: null,
        duration: null,
        toastNode: null,
        hasEntered: false,
    };

    componentDidMount() {
        const { position = 'bottomRight', isVisible = true } = this.props;
        const toastNode = getDomNode({ position, isVisible });
        this.setState(
            {
                toastNode,
            },
            () => {
                if (this.props.isVisible) {
                    popToast(this.state.toastNode);
                    this.handleEnter();
                    this.applyScrollHandler();
                }
            }
        );
    }

    componentDidUpdate(prevProps: ToastProps) {
        if (prevProps.isVisible !== this.props.isVisible) {
            if (this.props.isVisible) {
                popToast(this.state.toastNode);
                this.handleEnter();
                this.applyScrollHandler();
            } else {
                this.removeScrollHandler();
                if (![EXITING, CLOSING].includes(this.state.stageDirection)) {
                    this.handleClose(EXITING);
                }
            }
        }
        if (
            prevProps.closeAfterTimeout !== this.props.closeAfterTimeout &&
            this.props.closeAfterTimeout &&
            this.state.hasEntered
        ) {
            this.startCloseTimeout(this.props.closeAfterTimeout);
        }
    }

    componentWillUnmount() {
        if (this.state.timeoutID) {
            clearTimeout(this.state.timeoutID);
            this.setState({ timeoutID: null });
        }
        this.removeScrollHandler();
        removeToast(this.state.toastNode);
    }

    throttledHandleScroll: () => void;

    applyScrollHandler = () => {
        window.addEventListener('scroll', this.throttledHandleScroll);
    };

    removeScrollHandler = () => {
        window.removeEventListener('scroll', this.throttledHandleScroll);
    };

    handleEnter = () => {
        setTimeout(() => {
            this.setState({
                showTimeoutBar: !!this.props.closeAfterTimeout,
                stageDirection: ENTERING,
            });
        }, 10);
    };

    // re-position the toaster dom node in case the navbar has changed height due to scrolling
    handleScroll = () => {
        updateToasterPosition(this.props.position);
    };

    handleTransitionEnd = () => {
        const { onClose, closeAfterTimeout } = this.props;
        const { stageDirection } = this.state;
        if (stageDirection === ENTERING) {
            this.setState({ hasEntered: true });
            if (closeAfterTimeout) {
                this.startCloseTimeout(closeAfterTimeout);
            }
        } else if (stageDirection === CLOSING || stageDirection === EXITING) {
            if (this.state.timeoutID) {
                clearTimeout(this.state.timeoutID);
            }
            this.setState({ hasEntered: false, showTimeoutBar: false, timeoutID: null });
            if (onClose) {
                onClose();
            }
        }
    };

    handleClose = (stageDirection?: ExitStageDirections) => {
        if (!stageDirection || ![EXITING, CLOSING].includes(stageDirection)) {
            stageDirection = CLOSING;
        }
        this.setState({ stageDirection });
    };

    startCloseTimeout = (timeoutDuration?: number | boolean) => {
        timeoutDuration = typeof timeoutDuration === 'number' ? timeoutDuration : DEFAULT_TIMEOUT;
        // timeout necessary timeout bar to render before giving it a timeout
        // otherwise timeout bar initially renders with width 0 instead of
        // transitioning to length 0
        const startTimer = () =>
            setTimeout(() => {
                const timeoutID = setTimeout(() => {
                    this.setState({ timeoutID, stageDirection: EXITING });
                }, parseInt(timeoutDuration));
                this.setState({ timeoutID });
            }, 10);

        this.setState({ showTimeoutBar: true, duration: timeoutDuration }, startTimer);
    };

    render() {
        const {
            position = 'bottomRight',
            closeAfterTimeout = false,
            type = 'info',
            showCloseButton,
            children,
            isVisible,
        } = this.props;
        const { duration, stageDirection, showTimeoutBar, timeoutID } = this.state;
        const dataTn = `${type}-toast-${isVisible ? 'visible' : 'invisible'}`;

        const toast = (
            <div
                className={classnames(
                    stylesheet.container,
                    stylesheet[stageDirection],
                    stylesheet[position],
                    stylesheet[type]
                )}
                onTransitionEnd={this.handleTransitionEnd}
                data-tn={dataTn}
            >
                <div onTransitionEnd={e => e.stopPropagation()}>
                    {showTimeoutBar && duration && (
                        <TimeoutBar
                            className={stylesheet.timeoutContainer}
                            duration={duration}
                            type={type}
                            inProgress={!!timeoutID && this.state.hasEntered}
                        />
                    )}
                    {(showCloseButton || !closeAfterTimeout) && (
                        <CloseButton handleClose={this.handleClose} type={type} />
                    )}
                    {children}
                </div>
            </div>
        );
        if (this.state.toastNode) {
            // this component will render normally in the React tree but
            // will render into the DOM as a child of the second argument.
            // see toaster.js for information on the toastNode used in the Toast.
            // read more @ https://reactjs.org/docs/portals.html
            return ReactDOM.createPortal(toast, this.state.toastNode);
        }
        return null;
    }
}
